// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { useContext } from "react";

import PropTypes from "prop-types";

import {
  ColorField,
  ResolutionField,
  SelectField,
  TextField,
  EditorField,
  NumberField,
  UploaderField,
} from "components/form/fields";
import { Column, Row } from "components/layout";
import { HtmlTranslation, trans } from "src/translations";
import { snakeToCamelCase } from "utils/string";

import {
  ManifestEditCustomField,
  ManifestEditFieldReset,
  ManifestEditKeysField,
} from ".";
import { EditorThemeContext } from "../../ManifestData";
import { FIELD_DESCRIPTION, FIELDS } from "../constants";
import styles from "./ManifestFields.scss";

const getInputId = (name) => `${name}-input-id`;

const renderField = ({ name, type, values, min, meta: imageMeta }) => {
  let fieldRenderer;
  const props = {
    id: getInputId(name),
    look: "simple",
    name: name,
    required: false,
  };
  switch (type) {
    case "select":
      fieldRenderer = <SelectField {...props} values={values} />;
      break;
    case "color":
      fieldRenderer = (
        <ColorField
          {...props}
          popupPosition="left"
          presets={[
            { value: "#000000", label: "black" },
            { value: "#FFFFFF", label: "white" },
          ]}
        />
      );
      break;
    case "resolution":
      fieldRenderer = <ResolutionField {...props} />;
      break;
    case "number":
      fieldRenderer = <NumberField {...props} min={min} />;
      break;
    case "image":
      return (
        <UploaderField
          {...props}
          imageValidationMetadata={imageMeta}
          accept={["image/jpeg", "image/jpg", "image/png"]}
        />
      );
    default:
      fieldRenderer = <TextField {...props} />;
  }
  return (
    <>
      {fieldRenderer}
      <ManifestEditFieldReset name={name} />
    </>
  );
};

export const ManifestEditField = ({ name, ...rest }) => {
  const { editorTheme, toggleTheme } = useContext(EditorThemeContext);

  switch (name) {
    case FIELDS.key_remapping.name:
      return <ManifestEditKeysField name={name} />;
    case FIELDS.custom.name:
      return <ManifestEditCustomField name={name} />;
    case FIELDS.user_script.name:
      return (
        <EditorField
          name={name}
          type="javascript"
          look="simple"
          accept={["text/javascript"]}
          required={false}
          uploadActionText={trans.APP_DETAILS_MANIFEST__SCRIPT_UPLOAD()}
          editorTheme={editorTheme}
          toggleTheme={toggleTheme}
        />
      );
    default:
      return (
        <Row className={styles.formRow}>
          <Column className={styles.labelCol}>
            <label className={styles.label} htmlFor={getInputId(name)}>
              {snakeToCamelCase(name)}
            </label>
            {name !== FIELDS.key_remapping.name && (
              <HtmlTranslation
                transFunc={FIELD_DESCRIPTION[name]}
                component="p"
              />
            )}
          </Column>
          <Column className={styles.inputCol}>
            {renderField({ name, ...rest })}
          </Column>
        </Row>
      );
  }
};

ManifestEditField.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf([
    "array",
    "color",
    "image",
    "number",
    "select",
    "resolution",
    "javascript",
    "text",
  ]),
  values: PropTypes.array,
};
